import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { getAiDashboardPulse } from '../../services/api/implementation/impl';
import { AiInsightError, AiInsightSkeleton, resolveAiProjectId } from './AiUiShared';
import { buildAiLeadsListPath } from './aiListDeepLinks';

const CACHE_STALE_MS = 15 * 60 * 1000;

/**
 * Compact AI summary strip for section pages (priorities, targets, receivables, etc.).
 * Uses the same pulse API as the main dashboard with a fixed vertical lens.
 */
export default function AiSectionBanner({
  projectId,
  vertical = 'management',
  surface = null,
  className = 'mb-4'
}) {
  const navigate = useNavigate();
  const apiProjectId = resolveAiProjectId(projectId);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['aiDashboardPulse', apiProjectId, vertical, 'section', surface],
    queryFn: () => getAiDashboardPulse(apiProjectId, vertical, surface),
    staleTime: CACHE_STALE_MS
  });

  if (isLoading) {
    return (
      <div className={`rounded-xl border border-teal-100 bg-teal-50/40 px-4 py-3 ${className}`}>
        <AiInsightSkeleton lines={2} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={className}>
        <AiInsightError
          message={error?.response?.data?.message || error?.message}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  const headline = data?.headline || 'AI brief';
  const summary = data?.summaryBox || '';
  const hotLeads = data?.metrics?.hotLeads;
  const followUps = data?.metrics?.followUpsDueToday;
  const overdue = data?.metrics?.overdueCollection;
  const ucLeads = data?.metrics?.underConsiderationLeads;

  const goToLeads = (aiFilter) => {
    navigate(buildAiLeadsListPath({ aiFilter, projectId: apiProjectId }));
  };

  if (!summary && !headline) return null;

  return (
    <div
      className={`rounded-xl border border-teal-100 bg-gradient-to-r from-teal-50/80 to-sky-50/50 px-4 py-3 ${className}`}>
      <div className="flex items-start gap-3">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#1E4A53]" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900">{headline}</p>
          {summary ? (
            <p className="mt-1 line-clamp-3 text-xs leading-relaxed text-gray-600">{summary}</p>
          ) : null}
          {(hotLeads != null || followUps != null || overdue != null || ucLeads != null) && (
            <div className="mt-2 flex flex-wrap gap-2">
              {hotLeads != null && hotLeads > 0 ? (
                <button
                  type="button"
                  onClick={() => goToLeads('hot')}
                  className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-red-700 ring-1 ring-red-100 hover:bg-red-50">
                  {hotLeads} hot
                </button>
              ) : null}
              {ucLeads != null && ucLeads > 0 ? (
                <button
                  type="button"
                  onClick={() => goToLeads('uc')}
                  className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-violet-800 ring-1 ring-violet-100 hover:bg-violet-50">
                  {ucLeads} UC pending
                </button>
              ) : null}
              {followUps != null && followUps > 0 ? (
                <button
                  type="button"
                  onClick={() => goToLeads('overdue')}
                  className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-amber-800 ring-1 ring-amber-100 hover:bg-amber-50">
                  {followUps} follow-ups due
                </button>
              ) : null}
              {overdue != null && overdue > 0 ? (
                <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-rose-800 ring-1 ring-rose-100">
                  ₹{Number(overdue).toLocaleString('en-IN')} overdue
                </span>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
